// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
import { toCabinetPoint, toRoomPoint, type Placement, type MutablePoint } from './placement'
import type { CabinetDimensions } from '../world/cabinet'
import { COMPACT_MENU } from './menuLayout'

export const NORMAL_MENU_SCALE = 1.3
export const MIN_NORMAL_MENU_SCALE = 1.05
export const QUICK_MENU_SCALE = 1.05

/** Fit the whole normal assembly once; never trade away the stand clearance or minimum targets. */
export function openingMenuCeiling(bottom:MutablePoint,head:MutablePoint,forward:MutablePoint,verticalFov=80):number {
  const pitch=Math.atan2(forward.y,Math.hypot(forward.x,forward.z))
  const fov=Number.isFinite(verticalFov)&&verticalFov>=20&&verticalFov<=120?verticalFov:80
  const upper=Math.max(-Math.PI*.45,Math.min(Math.PI*.45,pitch+(fov*.5-2)*Math.PI/180))
  return head.y+Math.hypot(bottom.x-head.x,bottom.z-head.z)*Math.tan(upper)
}
export function fitNormalMenuScale(bottom:MutablePoint,head:MutablePoint,forward:MutablePoint,verticalFov=80):number {
  return Math.max(MIN_NORMAL_MENU_SCALE,Math.min(NORMAL_MENU_SCALE,(openingMenuCeiling(bottom,head,forward,verticalFov)-bottom.y)/(.141+.11)))
}

/** Reflow at a fixed cabinet stand when the protected square assembly cannot fit. */
export function normalMenuShape(bottom:MutablePoint,head:MutablePoint,forward:MutablePoint,verticalFov=80) {
  const scale=fitNormalMenuScale(bottom,head,forward,verticalFov)
  const compact=bottom.y+.251*scale>openingMenuCeiling(bottom,head,forward,verticalFov)+1e-6
  return {compact,scale:compact?1:scale,belowCentre:compact?COMPACT_MENU.totalHeight-COMPACT_MENU.height/2:.141*scale,
    aboveCentre:compact?COMPACT_MENU.height/2:.11*scale}
}

/** Quick follows the palm; only Full uses the cabinet stand. Very close hands keep24cm eye clearance. */
export function quickMenuCentre(_dimensions: CabinetDimensions, _placement: Placement, head: MutablePoint, palm: MutablePoint, viewerRight: MutablePoint = {x:1,y:0,z:0}): MutablePoint {
  const x=palm.x-head.x,y=palm.y-head.y,z=palm.z-head.z,length=Math.hypot(x,y,z)
  if(length<.001)return {x:head.x,y:head.y,z:head.z+.24}
  const distance=Math.max(.24,length-.08)
  const horizontal=Math.hypot(viewerRight.x,viewerRight.z),rx=horizontal>.001?viewerRight.x/horizontal:1,rz=horizontal>.001?viewerRight.z/horizontal:0
  let px=head.x+x*distance/length+rx*.055,py=head.y+y*distance/length+.08,pz=head.z+z*distance/length+rz*.055
  const ex=px-head.x,ey=py-head.y,ez=pz-head.z,reach=Math.hypot(ex,ey,ez)
  if(reach<.24){px=head.x+ex*.24/reach;py=head.y+ey*.24/reach;pz=head.z+ez*.24/reach}
  return {x:px,y:py,z:pz}
}

/** 60ms filter; protect the current palm after filtering, rather than letting lag put it through the card. */
export function followQuick(previous: MutablePoint,target: MutablePoint,head: MutablePoint,palm: MutablePoint,seconds: number): MutablePoint {
  const dt=Math.max(0,seconds),alpha=dt/(.06+dt)
  let x=previous.x+(target.x-previous.x)*alpha,y=previous.y+(target.y-previous.y)*alpha,z=previous.z+(target.z-previous.z)*alpha
  let dx=head.x-palm.x,dy=head.y-palm.y,dz=head.z-palm.z
  const length=Math.hypot(dx,dy,dz)
  if(length>.001){
    dx/=length;dy/=length;dz/=length
    const front=(x-palm.x)*dx+(y-palm.y)*dy+(z-palm.z)*dz
    const correction=Math.max(0,Math.min(.045,Math.max(0,length-.24))-front)
    x+=dx*correction;y+=dy*correction;z+=dz*correction
  }
  const ex=x-head.x,ey=y-head.y,ez=z-head.z,reach=Math.hypot(ex,ey,ez)
  if(reach<=.001)return target
  if(reach<.24){x=head.x+ex*.24/reach;y=head.y+ey*.24/reach;z=head.z+ez*.24/reach}
  return {x,y,z}
}

/** Lowest assembly point: cabinet height/incline/scale own clearance, opening head bounds reach. */
export function menuStandBottom(dimensions: CabinetDimensions, placement: Placement, head: MutablePoint): MutablePoint {
  const front = dimensions.length / 2
  // A rotated near edge may lie almost under the eye; aim at the cabinet centre.
  const edge=toRoomPoint(placement,0,dimensions.lockdownBarHeight,0,{x:0,y:0,z:0})
  let dx=edge.x-head.x,dz=edge.z-head.z,length=Math.hypot(dx,dz)
  if(length<.001){const inward=toRoomPoint(placement,0,0,-.1,{x:0,y:0,z:0});dx=inward.x-edge.x;dz=inward.z-edge.z;length=Math.hypot(dx,dz)}
  dx/=length;dz/=length
  const x=head.x+dx*.46,z=head.z+dz*.46
  let depth=0
  for(const side of [-.25,.25]) {
    const corner=toCabinetPoint(placement,x+dz*side,head.y,z-dx*side,{x:0,y:0,z:0})
    depth=Math.max(depth,Math.min(dimensions.length,front-corner.z))
  }
  const y = dimensions.lockdownBarHeight + depth * Math.tan(dimensions.incline)
    + .024 + .15 / placement.scale
  return {x,y:Math.max(placement.y+y*placement.scale,head.y-.62),z}
}
